// Company (tenant) settings — things that apply to every site (mockup admin-v1/settings-v2
// "Bolag", owner 2026-10-30). Stored as JSON in tenant_config.config, every field with a default.
import { z } from "zod";
export const TenantConfig = z
    .object({
    profiles: z
        .object({
        /** The company's default handling profile (name of a published profile); null = none set. */
        defaultProfile: z.string().trim().min(1).nullable().default(null),
        /** May sites, the division's rules and articles point out another profile? Locked by default. */
        allowBelow: z.boolean().default(false),
        /** The classification dimension (key) whose values may point out profiles; null = none. Kept for
         *  older readers — `dimensions` is the list; this mirrors its first entry. */
        dimension: z.string().trim().min(1).max(40).nullable().default(null),
        /** The chain of dimensions that may point out profiles, in order (owner 2026-11-03: Gr first, then
         *  the product category): a value without a rule passes the decision on; a rule may lock what is below. */
        dimensions: z.array(z.string().trim().min(1).max(40)).max(8).default([]),
    })
        .default({}),
    /** The company as it appears on documents (owner 2026-10-31: settings feed the standard
     *  delivery note; the template tool later binds the same fields). */
    company: z
        .object({
        name: z.string().trim().max(120).default(""),
        street: z.string().trim().max(120).default(""),
        postcode: z.string().trim().max(20).default(""),
        city: z.string().trim().max(80).default(""),
        orgNo: z.string().trim().max(40).default(""),
        phone: z.string().trim().max(40).default(""),
        email: z.string().trim().max(120).default(""),
        bankgiro: z.string().trim().max(40).default(""),
        plusgiro: z.string().trim().max(40).default(""),
        fTax: z.boolean().default(true),
        seat: z.string().trim().max(80).default(""),
        /** PNG or JPEG as a data URL, printed top left. */
        logo: z.string().max(400_000).nullable().default(null),
    })
        .default({}),
    /** Delivery note numbering (owner 2026-10-31: Trail's own series, continuing the old system's). */
    deliveryNote: z
        .object({ prefix: z.string().trim().max(10).default(""), startNumber: z.number().int().min(1).default(1) })
        .default({}),
    /** The mail service automations send through (owner 2026-11-01): none, SMTP, or Microsoft 365
     *  via Graph with Trail's Entra app (Mail.Send). The password lives encrypted in mail_secret. */
    mail: z
        .object({
        kind: z.enum(["none", "smtp", "graph"]).default("none"),
        from: z.string().trim().max(200).default(""),
        smtpHost: z.string().trim().max(200).default(""),
        smtpPort: z.number().int().min(1).max(65535).default(587),
        smtpUser: z.string().trim().max(200).default(""),
    })
        .default({}),
    /** The ERP connection's behaviour on the floor (owner 2026-10-30). */
    /** Security (docs/security.md): a desktop session locks after this many idle minutes; 0 = never. */
    security: z
        .object({
        idleMinutes: z.number().int().min(0).max(1440).default(0),
    })
        .default({}),
    pallets: z
        .object({
        /** A pallet whose last kolli has left is retired (state emptied) at once (owner 2026-11-02: better
         *  that it no longer exists in Trail, so moving something onto it says so). Off: it stays until someone retires it. */
        retireWhenEmpty: z.boolean().default(true),
    })
        .default({}),
    erp: z
        .object({
        /** Read the order from the ERP again before receiving or picking starts — operators wait a
         *  few seconds, the order is current. No-op for a company without a mirror. */
        refreshOrderBeforeWork: z.boolean().default(true),
        /** Where an order line's GTIN comes from (owner 2026-11-03: "GTIN skall aldrig plockas från rader utan
         *  alltid från artikeln" — configurable): "register" = the article's own GTIN in Trail's register, the
         *  line's value is ignored; "line" = the ERP's line value when it has one, else the register. */
        lineGtin: z.enum(["register", "line"]).default("register"),
        /** Customer returns (owner 2026-11-03). warehousePerLine: the receiver chooses the ERP warehouse when
         *  Trail creates a line (the head's proposed); off = everything to the head's warehouse. */
        returns: z.object({ warehousePerLine: z.boolean().default(true) }).default({}),
        /** What Trail reports back to the ERP (owner 2026-11-01): the stock-bearing moments are the
         *  integration's contract — on by default, switchable per document kind here, never by an
         *  automation. `shadow` builds the ERP calls and shows them without sending; `live` sends. */
        /** Cancellation guard (owner 2026-11-02, after a full read with an empty open set cancelled every
         *  purchase order of the demo company): when one open-set import would cancel more than
         *  `maxCount` orders, or more than `maxShare` % of the open ones, nothing is cancelled — the
         *  set is held, the orders stay open and the office decides under Avvikelser. */
        cancelGuard: z
            .object({
            maxCount: z.number().int().min(0).max(100000).default(20),
            maxShare: z.number().int().min(1).max(100).default(25),
        })
            .default({}),
        report: z
            .object({
            mode: z.enum(["shadow", "live"]).default("shadow"),
            /** Pick and release are two steps in every ERP (owner 2026-11-01): the pick reserves the
             *  goods on the pallet, the release lets stock and invoicing go. false: Trail releases the
             *  order the moment it is picked (today's way); true: release is its own step, on the phone
             *  or the desktop, and the ERP gets two reports. */
            twoStepRelease: z.boolean().default(false),
            /** The ERP realising (invoicing) an order means the goods have left (owner 2026-11-02: at
             *  Kalundgren the invoice comes after the goods went, and nobody wants to scan them out):
             *  Trail ships the order's picked goods itself. Off: the goods stay until a ship step says so. */
            shipOnRealised: z.boolean().default(true),
            /** Write guard (owner 2026-11-02: "Trail får inte plötsligt börja skriva en massa till Visma"): more
             *  pending reports than this and nothing goes until the office releases them for a while. */
            maxPerRun: z.number().int().min(1).max(500).default(20),
            /** Set by "Släpp alla": claims may pass the guard until this instant. */
            releaseUntil: z.string().nullable().default(null),
            salesOrders: z.boolean().default(true),
            receipts: z.boolean().default(true),
            adjustments: z.boolean().default(true),
            /** Put-away and moves (owner 2026-11-04): the new location is written on the ERP's batch row. */
            moves: z.boolean().default(true),
        })
            .default({}),
    })
        .default({}),
})
    .strict();
/** The chain of dimensions that point out profiles, in order: the list, else the older single field. */
export function profileDimensions(p) {
    if (!p)
        return [];
    if (p.dimensions && p.dimensions.length)
        return [...new Set(p.dimensions)];
    return p.dimension ? [p.dimension] : [];
}
//# sourceMappingURL=tenant-config.js.map